<template>
  <Panel class="intro-y" :title="$t('text.loanDetails')">
    <div class="flex flex-col sm:flex-row">
      <p class="mr-auto text-gray-600">{{ $t('text.status') }}</p>
      <div class="font-medium">
        <div class="whitespace-nowrap">
          <div :class="['status-tag', `is-${getStatus(loan.status)}`]">
            {{ $t(`status.${getStatus(loan.status)}`) }}
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col sm:flex-row mt-5">
      <p class="mr-auto text-gray-600">{{ $t('text.loanNumber') }}</p>
      <p class="font-medium">{{ loan.id }}</p>
    </div>
    <div class="flex flex-col sm:flex-row mt-5">
      <p class="mr-auto text-gray-600">{{ $t('text.loanAmount') }}</p>
      <p class="font-medium">{{ loan.amount | currency }}</p>
    </div>
    <div class="flex flex-col sm:flex-row mt-5">
      <p class="mr-auto text-gray-600">{{ $t('text.loanDates') }}</p>
      <p class="font-medium">{{ `${loan.startDate} / ${loan.endDate}` }}</p>
    </div>
    <div class="flex flex-col sm:flex-row mt-5">
      <p class="mr-auto text-gray-600">{{ $t('text.loanFee') }}</p>
      <p class="font-medium">{{ loan.fee | currency }}</p>
    </div>
    <div class="flex flex-col sm:flex-row mt-5">
      <p class="mr-auto text-gray-600">{{ $t('text.loanInterestRate') }}</p>
      <p class="font-medium">{{ loan.interestRate }}%</p>
    </div>
    <div class="flex flex-col sm:flex-row mt-5">
      <p class="mr-auto text-gray-600">{{ $t('text.loanAmountRate') }}</p>
      <p class="font-medium">{{ loan.rateAmount | currency }}</p>
    </div>
    <div class="flex flex-col sm:flex-row mt-5">
      <p class="mr-auto text-gray-600">{{ $t('text.loanAmountIncreaseRate') }}</p>
      <p class="font-medium">{{ loan.increaseRateAmount | currency }}</p>
    </div>
    <template v-if="loan.status !== 'PAID'">
      <div class="flex flex-col sm:flex-row mt-5">
        <p class="mr-auto text-gray-600">{{ $t('text.loanAmountPay') }}</p>
        <p class="font-medium">{{ loan.payAmount | currency }}</p>
      </div>
      <div class="flex flex-col sm:flex-row mt-5">
        <p class="mr-auto text-gray-600">{{ $t('text.loanAmountExtension') }}</p>
        <p class="font-medium">{{ loan.extendAmount | currency }}</p>
      </div>
    </template>
    <div v-else class="flex flex-col sm:flex-row mt-5">
      <p class="mr-auto text-gray-600">{{ $t('text.loanAmountPaid') }}</p>
      <p class="font-medium">{{ loan.paidAmount | currency }}</p>
    </div>

    <div
      v-if="loan.status !== 'PAID' && !isCollectionsOfficer()"
      class="-mx-5 mt-5 p-4 pb-0 border-t border-gray-200 dark:border-dark-5"
    >
      <Button
        @click="onPay('pay')"
        class="border text-gray-700 dark:bg-dark-5 dark:text-gray-300 m-1"
      >
        {{ $t('text.doPay') }}
      </Button>
      <Button
        @click="onPay('extend')"
        class="border text-gray-700 dark:bg-dark-5 dark:text-gray-300 m-1"
      >
        {{ $t('text.doExtend') }}
      </Button>
      <Button
        @click="onPay('writeOff')"
        class="border text-gray-700 dark:bg-dark-5 dark:text-gray-300 m-1"
      >
        {{ $t('text.writeOff') }}
      </Button>
      <Button
        @click="onDownloadNotice"
        :loading="isDownloadingNotice"
        class="border text-gray-700 dark:bg-dark-5 dark:text-gray-300 m-1"
      >
        {{ $t('text.downloadNotice') }}
      </Button>
    </div>
    <div
      v-if="loan.status !== 'PAID'"
      class="-mx-5 mt-5 p-5 pb-0 border-t border-gray-200 dark:border-dark-5"
    >
      <div class="grid grid-cols-12 gap-3">
        <div class="col-span-12 xl:col-span-6">
          <Button
            @click="sendSMS('extend')"
            :loading="isSendingExtendAmount"
            class="w-full bg-theme-17 text-theme-11"
          >
            {{ $t('text.sendExtendAmountBySMS') }}
          </Button>
        </div>
        <div class="col-span-12 xl:col-span-6">
          <Button
            @click="sendSMS('total')"
            :loading="isSendingTotalAmount"
            class="w-full bg-theme-14 text-theme-10"
          >
            {{ $t('text.sendTotalAmountBySMS') }}
          </Button>
        </div>
      </div>
    </div>
    <ModalEdit :id="modalId" :title="title" :loading="isPaying" @click="onSubmit">
      <div class="p-5">
        <InputDate v-model="$v.date.$model" :vuelidate="$v.date" :label="$t('text.date')" />
        <InputMoney
          v-model="$v.amount.$model"
          :vuelidate="$v.amount"
          :label="$t('text.loanAmountPay')"
        />
      </div>
    </ModalEdit>
  </Panel>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  LOAN_REPAYMENT_REQUEST,
  SEND_SMS_REQUEST,
  DOWNLOAD_LOAN_NOTICE_REQUEST,
} from '@/store/actions/loan';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import Panel from '@/components/ui/Panel';
import Button from '@/components/ui/button/Button';
import ModalEdit from '@/components/ui/ModalEdit';
import InputDate from '@/components/ui/input/InputDate';
import InputMoney from '@/components/ui/input/InputMoney';
import humps from 'humps';
import meMixin from '@/mixins/meMixin';

export default {
  mixins: [validationMixin, meMixin],

  props: {
    loan: Object,
  },

  validations() {
    return {
      amount: {
        required,
      },
      date: {
        required,
      },
    };
  },

  data() {
    return {
      amount: null,
      date: '',
      modalId: 'loan_payment_amount',
      isExtend: false,
      type: '',
    };
  },

  computed: {
    ...mapGetters([
      'loanStatus',
      'sendSMSStatus',
      'sendSMSType',
      'downloadLoanNoticeStatus',
      'downloadLoanNoticeLink',
    ]),
    isPaying() {
      return this.loanStatus === 'repaying';
    },
    isSendingExtendAmount() {
      return !!(this.sendSMSStatus === 'loading' && this.sendSMSType === 'extend');
    },
    isSendingTotalAmount() {
      return !!(this.sendSMSStatus === 'loading' && this.sendSMSType === 'total');
    },
    isDownloadingNotice() {
      return this.downloadLoanNoticeStatus === 'loading';
    },
    title() {
      if (this.type === 'extend') {
        return this.$t('text.doExtend');
      }
      if (this.type === 'writeOff') {
        return this.$t('text.writeOff');
      }
      return this.$t('text.doPay');
    },
  },

  methods: {
    getStatus(status) {
      return status ? humps.camelize(status).toLowerCase() : '';
    },
    async onPay(type) {
      this.type = type;
      await this.resetForm();
      cash(`#${this.modalId}`).modal('show');
    },
    sendSMS(type) {
      const payload = {
        id: this.loan.id,
        data: {
          type,
        },
      };
      this.$store.dispatch(SEND_SMS_REQUEST, payload);
    },
    async onDownloadNotice() {
      await this.$store.dispatch(DOWNLOAD_LOAN_NOTICE_REQUEST, this.loan.id);
      if (this.downloadLoanNoticeStatus === 'success') {
        window.open(`${this.downloadLoanNoticeLink}`, '_blank');
      }
    },
    resetForm() {
      this.amount = null;
      this.date = '';
      this.$v.$reset();
    },
    async onSubmit() {
      if (this.$v) {
        this.$v.$touch();
        if (this.$v.$anyError) {
          this.$toasted.show(this.$t('toast.failedForm'), { type: 'error' });
          return;
        }
      }

      let data = {
        amount: this.amount, // type is 'writeOff'
      };

      if (this.type === 'pay') {
        data = {
          paidAmount: this.amount,
        };
      } else if (this.type === 'extend') {
        data = {
          extendAmount: this.amount,
        };
      }

      const payload = {
        id: this.loan.id,
        type: this.type,
        data: { ...data, date: this.date },
      };

      await this.$store.dispatch(LOAN_REPAYMENT_REQUEST, payload);
      if (this.loanStatus === 'success') {
        cash(`#${this.modalId}`).modal('hide');
      }
    },
  },

  components: {
    Panel,
    Button,
    ModalEdit,
    InputDate,
    InputMoney,
  },
};
</script>
